 
import React, { useState, FC } from 'react';
import AUReceiptComponent from '@aarhus-university/au-lib-react-components/src/components/AUReceiptComponent';
import AUNotificationComponent from '@aarhus-university/au-lib-react-components/src/components/AUNotificationComponent';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import MailSentReceiptComponent from '../common/MailSentReceiptComponent';
import {
  auIdRegex, emailRegex, renderValidation, validateForm,
} from '../../lib/validation';
import { useRequestPasswordRecoveryMutation } from '../../services/api';
import { useTranslationsContext } from '../../lib/translation';

interface Props {
  contextKey?: string | null;
}

const ForgotPasswordComponent: FC<Props> = ({ contextKey }) => {
  const lang = useLangContext();
  const translations = useTranslationsContext();
  const [privateMailAddress, setPrivateMailAddress] = useState('');
  const [privateMailValidationMessages, setPrivateMailValidationMessages] = useState<string[]>([]);
  const [isPrivateMailValid, setIsPrivateMailValid] = useState(false);

  const [recover, response] = useRequestPasswordRecoveryMutation();
  const { isUninitialized, isSuccess, isLoading, isError } = response;
  const saving = !isUninitialized && isLoading;
  const saved = !isUninitialized && isSuccess;
  const recovery = translations.account_activation.token.recovery;
  const recoveryLink = `/${lang}/password/forgot${contextKey ? `?context=${encodeURIComponent(contextKey)}` : ''}`;

  const validationRules: IValidationRule[] = [{
    message: contextKey ? recovery.invalid_email : translations.forgot.validation.invalid,
    rule: (fieldValue: string) => {
      const trimmed = fieldValue.trim();
      const validMail = trimmed.match(emailRegex) !== null;
      const validAuId = trimmed.match(auIdRegex) !== null;
      return validMail || (!contextKey && validAuId);
    },
  }];

  const validatePrivateMail = (value: string, alert = true) => {
    const [valid, messages] = validateForm(value, validationRules, translations.forgot.validation.valid);
    setPrivateMailAddress(value);
    setPrivateMailValidationMessages(alert ? messages : []);
    setIsPrivateMailValid(valid);
  };

  const handleSubmit = () => {
    validatePrivateMail(privateMailAddress, !isPrivateMailValid);
    if (isPrivateMailValid) {
      recover({
        privateEmail: privateMailAddress,
        ...(contextKey ? { context: contextKey } : {}),
      });
    }
  };

  if (saved) {
    return (
      <>
        <div className="page__content__block">
          <AUReceiptComponent
            receiptText={translations.forgot.receipt.header_password}
            helpText={<p className="text--larger">{contextKey ? recovery.receipt_text : translations.forgot.receipt.text}</p>}
            showGoTo={false}
          />
        </div>
        <MailSentReceiptComponent
          mailReceiptHelp={contextKey ? translations.forgot.receipt.wrong_mail : translations.forgot.receipt.wrong_mail_username}
          goBackLink={recoveryLink}
        />
      </>
    );
  }

  let privateMailValidationClassName = '';
  if (!isPrivateMailValid && privateMailValidationMessages.length) {
    privateMailValidationClassName = ' form-info--error';
  } else if (isPrivateMailValid) {
    privateMailValidationClassName = ' form-info--confirmed';
  }

  return (
    <div className="page__content__block">
      <h1>{contextKey ? recovery.header : translations.forgot.password.header}</h1>
      <p>{contextKey ? recovery.intro : translations.forgot.password.text}</p>
      {isError && (
        <AUNotificationComponent
          type="warning"
          header={recovery.request_error}
          content={[]}
        />
      )}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className={`form__field${privateMailValidationClassName}`}>
          <label key="label" htmlFor="mail-private">
            {translations.forgot.private_mail}
            <div className="form-info__hint">
              {contextKey ? recovery.intro : translations.forgot.private_mail_hint}
            </div>
            {renderValidation(privateMailValidationMessages)}
          </label>
          <input
            type="text"
            id="mail-private"
            value={privateMailAddress || ''}
            onChange={(e) => {
              validatePrivateMail(e.target.value.trim(), privateMailValidationMessages.length > 0);
            }}
            disabled={saving}
          />
        </div>
        <div className="submit">
          <div className={`${saving ? 'submit-input-wrapper--processing' : ''}`}>
            <input
              className="button"
              type="submit"
              value={contextKey ? recovery.action : translations.forgot.password.next}
              disabled={saving}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

ForgotPasswordComponent.displayName = 'ForgotPasswordComponent';
export default ForgotPasswordComponent;
